import React from 'react';
import { motion } from 'framer-motion';
import { ServerCrash, RefreshCw, LogOut, WifiOff, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';

export default function SystemErrorAlert({ 
  title = "Erreur de Connexion", 
  message = "Nous n'arrivons pas à joindre le serveur. Il se peut qu'il soit en maintenance ou que votre connexion internet soit instable.",
  showReload = true,
  showLogout = true
}) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleReload = () => {
    window.location.reload();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="relative overflow-hidden bg-gradient-to-b from-red-600 to-red-800 rounded-[2rem] shadow-[0_30px_60px_-15px_rgba(220,38,38,0.5)] border border-red-500/50 p-10 flex flex-col items-center text-center w-full"
    >
      {/* Reflet intérieur (lueur premium) */}
      <div className="absolute inset-0 rounded-[2rem] shadow-[inset_0_1px_1px_rgba(255,255,255,0.3)] pointer-events-none" />

      {/* Icône animée au centre */}
      <div className="relative mb-8 z-10 mt-2">
        <motion.div 
          animate={{ scale: [1, 1.05, 1], boxShadow: ["0px 0px 0px rgba(255,255,255,0)", "0px 0px 30px rgba(255,255,255,0.2)", "0px 0px 0px rgba(255,255,255,0)"] }} 
          transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
          className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 shadow-xl"
        >
          <ServerCrash className="w-12 h-12 text-white" strokeWidth={1.5} />
        </motion.div>
        
        {/* Petits badges autour de l'icône */}
        <motion.div 
          animate={{ opacity: [0.4, 1, 0.4] }} 
          transition={{ repeat: Infinity, duration: 1.5, delay: 0.5 }}
          className="absolute -top-2 -right-2 bg-red-900/80 backdrop-blur-sm p-2 rounded-full shadow-lg border border-red-500/50"
        >
          <WifiOff className="w-4 h-4 text-red-100" strokeWidth={2} />
        </motion.div>
        <motion.div 
          animate={{ opacity: [0.4, 1, 0.4] }} 
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="absolute -bottom-1 -left-2 bg-red-900/80 backdrop-blur-sm p-2 rounded-full shadow-lg border border-red-500/50"
        >
          <Activity className="w-4 h-4 text-red-100" strokeWidth={2} />
        </motion.div>
      </div>

      {/* Textes */}
      <h2 className="text-3xl font-extrabold text-white mb-3 z-10 tracking-tight">
        {title}
      </h2>
      <p className="text-red-100/90 mb-10 max-w-md z-10 leading-relaxed text-[15px] font-medium">
        {message}
      </p>

      {/* Actions */}
      <div className="w-full flex flex-col sm:flex-row gap-3 z-10">
        {showReload && (
          <button
            onClick={handleReload}
            className="flex-1 flex justify-center items-center rounded-2xl bg-white px-5 py-4 text-[15px] font-bold text-red-700 shadow-xl hover:bg-gray-50 focus:outline-none transition-all active:scale-[0.98]"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Réessayer
          </button>
        )}
        {showLogout && (
          <button
            onClick={handleLogout}
            className="flex-1 flex justify-center items-center rounded-2xl bg-white/10 px-5 py-4 text-[15px] font-bold text-white shadow-sm hover:bg-white/20 border border-white/20 focus:outline-none transition-all active:scale-[0.98]"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Déconnexion
          </button>
        )}
      </div>
      
      {/* Informations techniques */}
      <div className="mt-8 pt-5 border-t border-white/10 w-full text-center z-10">
        <span className="text-[11px] uppercase tracking-widest font-semibold text-red-200/60">
          Système déconnecté • Code : ERR_NETWORK
        </span>
      </div>
    </motion.div>
  );
}
